import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, money } from "../lib/api";

/**
 * Polls /api/orders/admin every 12 s and surfaces a toast (plus a system
 * notification when the tab is hidden) whenever a *new* order_id appears
 * compared to the previous poll.
 *
 * This is mounted from AppShell only when the active user is a company
 * admin, so the alert fires regardless of which admin sub-page they are on.
 *
 * The first poll seeds the "known" set without alerting; we don't want to
 * notify the admin about pre-existing orders the moment they log in. After
 * that, every additional order_id is treated as a fresh arrival.
 */
export function useNewOrderListener({ enabled }) {
  const knownIdsRef = useRef(null); // Set<string> | null  (null = uninitialised)
  const seededRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) {
      knownIdsRef.current = null;
      seededRef.current = false;
      return;
    }
    let cancelled = false;

    const pulse = async () => {
      try {
        const r = await api.get("/orders/admin", { params: { page: 1, page_size: 50, status: "active" } });
        const list = r.data?.items || [];
        if (cancelled) return;
        const incoming = new Set(list.map((o) => o.order_id));
        if (!seededRef.current) {
          knownIdsRef.current = incoming;
          seededRef.current = true;
          return;
        }
        const known = knownIdsRef.current || new Set();
        const fresh = list.filter((o) => !known.has(o.order_id));
        if (fresh.length) {
          fresh.forEach((o) => fireAlert(o, navigate));
        }
        knownIdsRef.current = incoming;
      } catch (_e) { /* ignore */ }
    };

    // Slight initial delay so we don't fire before the user has even seen
    // the first paint of the route they just landed on.
    const initial = setTimeout(pulse, 1500);
    const interval = setInterval(pulse, 12000);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [enabled, navigate]);
}

function fireAlert(o, navigate) {
  const sixHex = (o.order_id || "").slice(-6).toUpperCase();
  const summary = `${o.client_name || "Cliente"} · ${money(o.total)}`;
  toast.info(`Nuevo pedido #${sixHex}`, {
    description: summary,
    duration: 8000,
    action: {
      label: "Ver",
      onClick: () => navigate(`/admin/orders/${o.order_id}`),
    },
  });
  if (
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted" &&
    document.visibilityState === "hidden"
  ) {
    try {
      const n = new Notification(`Nuevo pedido #${sixHex}`, {
        body: summary,
        tag: `new-order-${o.order_id}`,
        renotify: true,
      });
      n.onclick = () => {
        window.focus();
        navigate(`/admin/orders/${o.order_id}`);
        n.close();
      };
    } catch (_e) { /* ignore */ }
  }
}
